import { Suspense, useState } from 'react';

import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import { TFormalOffers } from '@helpers/FormalOffers/formalOffers.types';
import { getFormalOffersLatestOfferConfiguration } from '@helpers/FormalOffers/getFormalOffersLatestOfferConfiguration';
import { getFormalOffersMaxAvailableCash } from '@helpers/FormalOffers/getFormalOffersMaxAvailableCash';
import { getTimeOutDate } from '@helpers/FormalOffers/getTimeOutDate';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import {
    fetchGetFormalOffers,
    fetchGetFormalOffersLatestOfferConfiguration,
    fetchPutFormalOffersCreditFacilityConfiguration
} from '@iwoca/lapi-client/edge';
import { Label } from '@iwoca/orion';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';

import { ApprovalHeader } from './ApprovalHeader/ApprovalHeader';
import { ErrorBoundary } from '../../../../../errorBoundary/ErrorBoundary';
import { useStateKey } from '../../../../../iwQueryClient/useStateKey';
import FallbackErrorModal from '../../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../../pageStructure/mainContentPending';
import { ChooseAmountInputWithSlider } from '../../../../ui/ChooseAmountInputWithSlider/ChooseAmountInputWithSlider';
import { isSelectedAmountValid } from '../../../../ui/ChooseAmountInputWithSlider/isSelectedAmountValid';
import { getMinAvailableCash } from '../../../MyOffers.helpers';

export const FlexiApprovedView = () => {
    return (
        <ErrorBoundary fallback={() => <FallbackErrorModal />}>
            <Suspense
                fallback={
                    <MainContentPending data-testid="flexi-approval-view-loading" />
                }
            >
                <FlexiApprovedViewComponent />
            </Suspense>
        </ErrorBoundary>
    );
};

const getSelectedOffer = (
    formalOffers: TFormalOffers,
    preSelectSplitTest: string
) => {
    if (preSelectSplitTest === 'INCLUDE') {
        const longestOffer = _.maxBy(formalOffers, (formalOffer) =>
            getDurationInMonths(formalOffer.duration!)
        );
        if (longestOffer && getDurationInMonths(longestOffer.duration!) >= 24)
            return longestOffer;
    }
    if (formalOffers.length === 1) {
        return formalOffers[0];
    }
    return undefined;
};

const FlexiApprovedViewComponent = () => {
    const stateKey = useStateKey();

    const { data: getFormalOffersResponse } = useQuery({
        queryKey: ['formal_offers', stateKey],
        queryFn: () => fetchGetFormalOffers({ stateKey }),
        suspense: true
    });

    const formalOffers = getFormalOffersResponse!.data.formal_offers;
    const maxCashAmount = getFormalOffersMaxAvailableCash(formalOffers);
    const timeoutDate = getTimeOutDate(formalOffers);

    const { data: latestOfferConfiguration } = useQuery({
        queryKey: ['fetchGetFormalOffersLatestOfferConfiguration', stateKey],
        queryFn: () =>
            fetchGetFormalOffersLatestOfferConfiguration({ stateKey }),
        select: (data) => data.data.latest_offer_configuration,
        suspense: true
    });

    const { previouslySelectedFormalOffer, previouslySelectedAmount } =
        getFormalOffersLatestOfferConfiguration({
            latestOfferConfiguration,
            formalOffers
        });

    const [drawdownAmount, setDrawdownAmount] = useState<number>(
        () => previouslySelectedAmount ?? getMinAvailableCash(formalOffers)
    );

    const preSelectSplitTest = useSplitTestBranch({
        test: 'offer_pre_selection_test_2025_q3',
        suspense: true
    });

    const selectedOffer =
        previouslySelectedFormalOffer ??
        getSelectedOffer(formalOffers, preSelectSplitTest);

    const updateDrawdownAmount = (amount: number) => {
        setDrawdownAmount(amount);

        if (selectedOffer && isSelectedAmountValid(amount, selectedOffer)) {
            fetchPutFormalOffersCreditFacilityConfiguration({
                stateKey,
                offerId: selectedOffer!.offer_id!,
                body: { data: { cash_amount: amount } }
            });
        }
    };

    return (
        <div>
            <ApprovalHeader
                approvalStatusTimeOut={timeoutDate}
                maxCashAmount={maxCashAmount}
            />
            <div className="px-l min-sm:px-0">
                <p className="text-2xl font-bold">
                    <FormattedMessage
                        id="_.flexi_approved.title"
                        defaultMessage="Let’s make your first withdrawal"
                    />
                </p>

                <div>
                    <ChooseAmountInputWithSlider
                        label={
                            <Label htmlFor="choose-amount">
                                <FormattedMessage
                                    id="_.flexi_approved.label"
                                    defaultMessage="How much would you like to withdraw today?"
                                />
                            </Label>
                        }
                        selectedAmount={drawdownAmount}
                        setSelectedAmount={updateDrawdownAmount}
                        maxAmount={maxCashAmount}
                    />
                </div>

                {/* TODO @k.anyabuike: Implement the offerChoiceButtons here */}
                {selectedOffer?.duration && (
                    <div className="mt-xl">
                        {getDurationInMonths(selectedOffer.duration)} months
                    </div>
                )}
            </div>
        </div>
    );
};
